import { estimateSmsSegments, type SmsEncoding } from './gsm7'

/** Default retail price per SMS segment (AUD). Matches phone integration COST_PER_SMS default. */
export const DEFAULT_SMS_COST_PER_SEGMENT_AUD = '0.07'

export function parseSmsCostPerSegmentCents(value?: string | null): number {
  const parsed = parseFloat(value ?? DEFAULT_SMS_COST_PER_SEGMENT_AUD)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return Math.round(parseFloat(DEFAULT_SMS_COST_PER_SEGMENT_AUD) * 100)
  }
  return Math.round(parsed * 100)
}

export type BulkSmsSegmentEncoding = SmsEncoding | 'mixed'

export type BulkSmsCostEstimate = {
  deliverableCount: number
  skippedCount?: number
  totalSegmentsLow: number
  totalSegmentsHigh: number
  /** Best-effort expected total segments (sum of per-recipient counts when known). */
  totalSegmentsExpected: number
  encoding: BulkSmsSegmentEncoding
  currency: string
  minorUnitsLow: number
  minorUnitsHigh: number
  /** Skewed expected charge in minor currency units for display. */
  minorUnitsExpected: number
}

export type AggregateBulkSmsCostEstimateInput = {
  /** Per-recipient rendered SMS bodies (preferred — yields exact segment totals). */
  renderedBodies?: string[]
  deliverableCount: number
  skippedCount?: number
  pricePerSegmentMinorUnits: number
  currency?: string
  /** Fallback when bodies are unavailable: min segments per message. */
  segmentsPerMessageLow?: number
  /** Fallback when bodies are unavailable: max segments per message. */
  segmentsPerMessageHigh?: number
}

function resolveEncoding(encodings: SmsEncoding[]): BulkSmsSegmentEncoding {
  if (encodings.length === 0) {
    return 'GSM-7'
  }

  const unique = new Set(encodings)
  if (unique.size === 1) {
    return encodings[0]!
  }

  return 'mixed'
}

function median(values: number[]): number {
  if (values.length === 0) {
    return 0
  }

  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)

  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1]! + sorted[mid]!) / 2
  }

  return sorted[mid]!
}

function mode(values: number[]): number {
  if (values.length === 0) {
    return 1
  }

  const counts = new Map<number, number>()
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1)
  }

  let bestValue = values[0]!
  let bestCount = 0

  for (const [value, count] of counts) {
    if (count > bestCount) {
      bestValue = value
      bestCount = count
    }
  }

  return bestValue
}

/** Skew segment totals toward the typical (mode/mean) case instead of pure min/max bounds. */
export function skewBulkSmsSegmentEstimate(params: {
  deliverableCount: number
  totalSegmentsLow: number
  totalSegmentsHigh: number
  segmentCounts?: number[]
}): number {
  const { deliverableCount, totalSegmentsLow, totalSegmentsHigh, segmentCounts } =
    params

  if (deliverableCount <= 0) {
    return 0
  }

  if (segmentCounts && segmentCounts.length > 0) {
    return segmentCounts.reduce((sum, count) => sum + count, 0)
  }

  if (totalSegmentsLow === totalSegmentsHigh) {
    return totalSegmentsLow
  }

  const perMessageLow = totalSegmentsLow / deliverableCount
  const perMessageHigh = totalSegmentsHigh / deliverableCount

  // Most templated SMS stay on the lower segment count; assume ~80% at min, ~20% at max.
  const skewedPerMessage = 0.8 * perMessageLow + 0.2 * perMessageHigh

  return Math.max(
    totalSegmentsLow,
    Math.min(totalSegmentsHigh, Math.round(skewedPerMessage * deliverableCount)),
  )
}

export function segmentsToMinorUnits(
  segments: number,
  pricePerSegmentMinorUnits: number,
): number {
  return Math.round(segments * pricePerSegmentMinorUnits)
}

/** Extrapolate expected total segments from a sample to the full deliverable cohort. */
export function estimateExpectedSegmentsFromSample(
  sampleSegmentCounts: number[],
  deliverableCount: number,
): number {
  if (sampleSegmentCounts.length === 0 || deliverableCount <= 0) {
    return 0
  }

  const avgSegmentsPerMessage =
    sampleSegmentCounts.reduce((sum, count) => sum + count, 0) /
    sampleSegmentCounts.length

  return Math.round(avgSegmentsPerMessage * deliverableCount)
}

/**
 * Aggregate SMS bulk cost from rendered bodies or segment bounds.
 * When rendered bodies are supplied, segment totals are exact; bounds collapse to that total.
 */
export function aggregateBulkSmsCostEstimate(
  input: AggregateBulkSmsCostEstimateInput,
): BulkSmsCostEstimate {
  const currency = input.currency ?? 'AUD'
  const deliverableCount = input.deliverableCount

  let segmentCounts: number[] | undefined
  let encodings: SmsEncoding[] = []
  let totalSegmentsLow: number
  let totalSegmentsHigh: number

  if (input.renderedBodies && input.renderedBodies.length > 0) {
    segmentCounts = input.renderedBodies.map(
      (body) => estimateSmsSegments(body).segments,
    )
    encodings = input.renderedBodies.map(
      (body) => estimateSmsSegments(body).encoding,
    )
    const totalSegments = segmentCounts.reduce((sum, count) => sum + count, 0)
    totalSegmentsLow = totalSegments
    totalSegmentsHigh = totalSegments
  } else {
    const perMessageLow = input.segmentsPerMessageLow ?? 1
    const perMessageHigh = Math.max(
      perMessageLow,
      input.segmentsPerMessageHigh ?? perMessageLow,
    )

    totalSegmentsLow = deliverableCount * perMessageLow
    totalSegmentsHigh = deliverableCount * perMessageHigh
    encodings = perMessageHigh > perMessageLow ? ['GSM-7', 'UCS-2'] : ['GSM-7']
  }

  const totalSegmentsExpected = skewBulkSmsSegmentEstimate({
    deliverableCount,
    totalSegmentsLow,
    totalSegmentsHigh,
    segmentCounts,
  })

  const minorUnitsLow = segmentsToMinorUnits(
    totalSegmentsLow,
    input.pricePerSegmentMinorUnits,
  )
  const minorUnitsHigh = segmentsToMinorUnits(
    totalSegmentsHigh,
    input.pricePerSegmentMinorUnits,
  )
  const minorUnitsExpected = segmentsToMinorUnits(
    totalSegmentsExpected,
    input.pricePerSegmentMinorUnits,
  )

  return {
    deliverableCount,
    ...(input.skippedCount !== undefined ? { skippedCount: input.skippedCount } : {}),
    totalSegmentsLow,
    totalSegmentsHigh,
    totalSegmentsExpected,
    encoding: resolveEncoding(encodings),
    currency,
    minorUnitsLow,
    minorUnitsHigh,
    minorUnitsExpected,
  }
}

/** Narrow segment bounds using sampled per-recipient counts (e.g. from a cohort preview). */
export function refineBulkSmsBoundsFromSamples(segmentCounts: number[]): {
  totalSegmentsLow: number
  totalSegmentsHigh: number
  totalSegmentsExpected: number
} {
  if (segmentCounts.length === 0) {
    return { totalSegmentsLow: 0, totalSegmentsHigh: 0, totalSegmentsExpected: 0 }
  }

  const totalSegmentsExpected = segmentCounts.reduce((sum, count) => sum + count, 0)
  const typicalPerMessage = Math.max(mode(segmentCounts), median(segmentCounts))

  return {
    totalSegmentsLow: segmentCounts.filter((count) => count <= typicalPerMessage).length > 0
      ? segmentCounts
          .filter((count) => count <= typicalPerMessage)
          .reduce((sum, count) => sum + count, 0)
      : Math.min(...segmentCounts),
    totalSegmentsHigh: totalSegmentsExpected,
    totalSegmentsExpected,
  }
}

export function buildBulkSmsEstimateSummary(
  input: AggregateBulkSmsCostEstimateInput,
): {
  deliverableCount: number
  skippedCount?: number
  totalSegmentsLow: number
  totalSegmentsHigh: number
  totalSegmentsExpected: number
  encoding: BulkSmsSegmentEncoding
  region: 'AU'
  costCentsLow: number
  costCentsHigh: number
  costCentsExpected: number
  currency: string
} {
  const estimate = aggregateBulkSmsCostEstimate(input)

  return {
    deliverableCount: estimate.deliverableCount,
    ...(estimate.skippedCount !== undefined
      ? { skippedCount: estimate.skippedCount }
      : {}),
    totalSegmentsLow: estimate.totalSegmentsLow,
    totalSegmentsHigh: estimate.totalSegmentsHigh,
    totalSegmentsExpected: estimate.totalSegmentsExpected,
    encoding: estimate.encoding,
    region: 'AU',
    costCentsLow: estimate.minorUnitsLow,
    costCentsHigh: estimate.minorUnitsHigh,
    costCentsExpected: estimate.minorUnitsExpected,
    currency: estimate.currency,
  }
}

/** Pick up to `maxSample` evenly spaced IDs for a representative estimate sample. */
export function pickEvenlySpacedSample<T>(items: T[], maxSample: number): T[] {
  if (items.length <= maxSample) {
    return items
  }

  const step = items.length / maxSample
  const sample: T[] = []

  for (let index = 0; index < maxSample; index += 1) {
    sample.push(items[Math.floor(index * step)]!)
  }

  return sample
}

/**
 * Extrapolate bulk SMS cost from a small sample to the full cohort size.
 * Deliverable/skipped counts and segment bounds scale linearly from sample rates.
 */
export function extrapolateBulkSmsCostEstimateFromSample(params: {
  totalRecipientCount: number
  sampleDeliverableCount: number
  sampleSkippedCount: number
  sampleRenderedBodies: string[]
  pricePerSegmentMinorUnits: number
  currency?: string
}): BulkSmsCostEstimate {
  const sampleSize = params.sampleDeliverableCount + params.sampleSkippedCount

  if (params.totalRecipientCount <= 0 || sampleSize <= 0) {
    return aggregateBulkSmsCostEstimate({
      deliverableCount: 0,
      skippedCount: params.totalRecipientCount,
      pricePerSegmentMinorUnits: params.pricePerSegmentMinorUnits,
      currency: params.currency,
    })
  }

  if (params.totalRecipientCount <= sampleSize) {
    return aggregateBulkSmsCostEstimate({
      renderedBodies: params.sampleRenderedBodies,
      deliverableCount: params.sampleDeliverableCount,
      skippedCount: params.sampleSkippedCount,
      pricePerSegmentMinorUnits: params.pricePerSegmentMinorUnits,
      currency: params.currency,
    })
  }

  const deliverableRate = params.sampleDeliverableCount / sampleSize
  const extrapolatedDeliverable = Math.round(
    deliverableRate * params.totalRecipientCount,
  )
  const extrapolatedSkipped = Math.max(
    0,
    params.totalRecipientCount - extrapolatedDeliverable,
  )

  if (params.sampleRenderedBodies.length === 0 || extrapolatedDeliverable <= 0) {
    return aggregateBulkSmsCostEstimate({
      deliverableCount: extrapolatedDeliverable,
      skippedCount: extrapolatedSkipped,
      pricePerSegmentMinorUnits: params.pricePerSegmentMinorUnits,
      currency: params.currency,
      segmentsPerMessageLow: 1,
      segmentsPerMessageHigh: 1,
    })
  }

  const segmentCounts = params.sampleRenderedBodies.map(
    (body) => estimateSmsSegments(body).segments,
  )
  const segmentsPerMessageLow = Math.min(...segmentCounts)
  const segmentsPerMessageHigh = Math.max(...segmentCounts)

  return aggregateBulkSmsCostEstimate({
    deliverableCount: extrapolatedDeliverable,
    skippedCount: extrapolatedSkipped,
    pricePerSegmentMinorUnits: params.pricePerSegmentMinorUnits,
    currency: params.currency,
    segmentsPerMessageLow,
    segmentsPerMessageHigh,
  })
}
